import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticFormModal, Fetching, InfiniteScroll, ListFilter, ListRow, NoResults, Search, Tag, WithSelectable, WithTooltip } from 'basic-components';
import { Exit, Refresh } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { DropdownService, Fetching as FetchingPrimary, globalActions } from 'primary-components';

import { basicCustomInsightConsts } from 'actions/modules/insight';
import getApiConfig from 'api/ApiConfig';
import { insightQueries } from 'queries/modules/insight';
import { sendEvent } from 'services/GAService';

import { useQueryClient } from '@tanstack/react-query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import FilterCarousel from 'primary/FilterCarousel';
import '../../Modal.scss';
import './Modal.scss';

const INSIGHT_FILE_FIELDS = basicCustomInsightConsts.INSIGHT_FILE_FIELDS
const getUrl = (groupName, insightName) => DashboardLinkFactory.insight.view({ groupName, name: insightName })
const Modal = ({ optionalText = undefined, closeModal, onSubmit, defaultValue = "" }) => {
  const dispatch = useDispatch(),
        [value, setValue] = useState(defaultValue),
        [query, setQuery] = useState(""),
        [groupName, setGroupName] = useState(),
        [tags, setTags] = useState([]),
        activeFilterCount = Number(Boolean(groupName)) + tags.length,
        clearAllFilters = useCallback(() => {
          setGroupName(undefined)
          setTags([])
        }, []),
        
        onSubmitLocal = useCallback(() => {
          onSubmit(value)
          closeModal()
        }, [closeModal, onSubmit, value]),
        
        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "" || value === " ",
          noDebounce: true
        }), [value]),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(provider => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch]),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, hasNextPage: hasNextPageGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = [] } = DropdownService.useQueryGroups({
          getApiConfig: getApiConfigLocal,
          input: groupQuery,
          queryClient
        }),
        [tagQuery, setTagQuery] = useState(),
        finalQuery = useMemo(() => {
          const queryArr = ["fields.isDraft=false"]
          let localQuery = undefined
          if(groupName !== "" && groupName !== undefined) {
            queryArr.push(`groupName.keyword=${groupName}`)
          }
          (tags  || []).forEach(t => {
            if(t !== "" && t !== undefined) {
              queryArr.push(`fields.tags=*${t}*`)
            }
          })

          if(query !== "") {
            queryArr.push(String(query).includes("=") ? query : `*${query}*`)
          }

          if(queryArr.length === 0) {
            localQuery = "*"
          } else {
            localQuery = queryArr.join("&")
          } 
          return localQuery;
        }, [groupName, query, tags]),
        onSelect = useCallback(response => (response?.pages || []).flatMap(p => p.items || []).map(item => Object.assign({}, item, { name: String(item.fileName).replace(".json", "") })), []),
        getQueryBuilder = useCallback(() => 
            new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        { data: tagsOptions = [], isPending: isFetchingTags } = insightQueries.useGetInsightTags({
          query: tagQuery ? `*${tagQuery}*` : "*",
          getQueryBuilder
        }),
        { data: insights, fetchNextPage: onScroll, hasNextPage, isPending, isFetchingNextPage, isRefetching, refetch } = insightQueries.useSearchInsights({ 
          query: finalQuery, 
          getQueryBuilder, 
          shouldBeLimited,
          onSelect
        }),
        addTagToFilter = useCallback(tag => setTags(currentTags => currentTags.includes(tag) ? currentTags : [...currentTags, tag]), []),
        preventFilterSubmit = useCallback(event => event.preventDefault(), []),
        scrollContRef = useRef()

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "insight_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className="ng-common-picker__modal ng-insight-picker__modal"
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmitLocal}
        optionalText={optionalText || " "}
        submitProps={submitProps}
        submitText="Select"
        title={"Insight picker"}
    >
      <div className="ng-file-picker__modal__top">
        <div className="ng-file-picker__modal__top__search">
          <Search 
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            activeFilterCount={activeFilterCount}
            className="ng-file-picker__modal__filters"
            onClearAll={clearAllFilters}
            onClickCapture={preventFilterSubmit}
        >
          <ListFilter
              defaultValue={groupName}
              fetchNextPage={fetchMoreGroups}
              hasNextPage={hasNextPageGroups}
              isFetching={isFetchingGroups || isFetchingNextPageGroups}
              label="Group"
              name="groupName"
              onChange={setGroupName}
              onInputChange={setGroupQuery}
              options={groups}
          />
          <ListFilter
              defaultValue={tags}
              isFetching={isFetchingTags}
              label="Tags"
              multi
              name="tags"
              onChange={setTags}
              onInputChange={setTagQuery}
              options={tagsOptions}
          />
        </FilterCarousel>
      </div>
      <div className="ng-common-picker__modal__headers">
        <div className="ng-common-picker__modal__headers__cell">
          Name
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Group
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Tags
        </div>
        <WithTooltip
            data-place="left"
            short
            text={isRefetching || isPending || isFetchingNextPage ? "Data is refreshing" : "Refresh data"}
        >
          <div 
              className="ng-common-picker__modal__headers__cell"
              onClick={isRefetching || isPending || isFetchingNextPage ? undefined : refetch}
          >
            {isRefetching || isPending || isFetchingNextPage ? (
              <Fetching 
                  loading={isFetchingNextPage || isPending || isRefetching}
                  size={12}
              />
            ) : (
              <Refresh
                  color="blue-bright"
                  height={16}
                  width={16}
              />
            )}
          </div>
        </WithTooltip>
      </div>
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={onScroll}
            threshold={400}
            useWindow={false}
        >
          {(insights || []).map((insight, index) => (
            <WithSelectable
                index={index}
                key={`File${index}`}
                selected={value?.name === insight.name && insight.groupName === value?.groupName}
                setSelected={() => setValue(insight)}
            >
              <ListRow
                  className="ng-common-picker__modal__content__scroll__row"
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(insight)
                      onSubmitLocal()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(insight)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {insight.name}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {insight.groupName}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {(insight.fields?.tags ? String(insight.fields?.[INSIGHT_FILE_FIELDS.TAGS]).split(" ") : []).map((tag, tagIndex) => (
                    <Tag
                        isEditable={false}
                        key={tagIndex}
                        onClick={event => {
                          event.preventDefault()
                          event.stopPropagation()
                          addTagToFilter(tag)
                        }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
                <WithTooltip
                    data-place="top"
                    short
                    text="Open insight"
                >
                  <div 
                      className="ng-common-picker__modal__exit"
                      onClick={() => CommonService.openInNewTab(getUrl(insight.groupName, insight.name))}
                  >
                    <Exit
                        color="blue-bright"
                        height={16}
                        width={16}
                    />
                  </div>
                </WithTooltip>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <FetchingPrimary isFetching={isPending}/>
        <When condition={(insights || []).length === 0 && !(isFetchingNextPage || isPending || isRefetching)}>
          {() => (
            <NoResults isModal/>
          )}
        </When>
      </div>
    </ElasticFormModal>
    
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
}

export default memo(Modal);
